import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { isArray, useCurrency } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { Button } from 'ui/atoms'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import DefaultTable from 'ui/components/CustomTable/CustomTable'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { getBidColorBasedOnOrderAndType } from 'ui/uiConfig/components/wildcardItems.helper'
import { useSnapshot } from 'valtio'
import {
  FormattedBidWithIdAndTitle,
  getScenarioName,
} from '../../../DayAheadBidManagement/DayAheadBidManagement.helper'
import BidModal from '../BidModal/BidModal'
import { getBidTagProductionConsumption } from '../bidTagSelection.helper'
import type { BidModalTagSelectionContext } from '../DayAheadBidEditor.types'

type BlockBidProps = {
  blockBids: FormattedBidWithIdAndTitle[]
  createBid: (bid: FormattedBidWithIdAndTitle) => void
  updateBid: (bid: FormattedBidWithIdAndTitle) => void
  deleteBid: (bid: FormattedBidWithIdAndTitle) => void
  disabledBidButtons?: boolean
  bidModalTagSelectionContext: BidModalTagSelectionContext
}
export default function BlockBidTable({
  blockBids,
  createBid,
  updateBid,
  deleteBid,
  disabledBidButtons,
  bidModalTagSelectionContext,
}: BlockBidProps): ReactElement {
  const { t } = useTranslation()

  const { activeSystem } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const elplanSnap = useSnapshot(elplanStore)

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [currentBlockBid, setCurrentBlockBid] = useState<FormattedBidWithIdAndTitle>()

  function editBlockBid(currentBlockBid: FormattedBidWithIdAndTitle): void {
    setCurrentBlockBid(currentBlockBid)
    setModalIsOpen(true)
  }

  function closeModal(): void {
    setCurrentBlockBid(undefined)
    setModalIsOpen(false)
  }

  function getType(type: string): 'block' | 'spot' | 'profile' | undefined {
    switch (type) {
      case 'blockbid':
        return 'block'
      case 'spotbid':
        return 'spot'
      case 'profilebid':
        return 'profile'
    }
  }

  function getBidInputCurrency(type: string | undefined): string {
    if (type === 'profilebid') {
      return bidModalTagSelectionContext.bidInputCurrencies.profile
    }
    return bidModalTagSelectionContext.bidInputCurrencies.block
  }

  const systemCurrency = useCurrency()
  const bidInputCurrency = bidModalTagSelectionContext.bidInputCurrencies.block
  const currency = bidInputCurrency ?? systemCurrency

  const columns = [
    {
      accessor: 'color',
      header: '',
      Cell: (tableContent: any) => {
        const order = tableContent.row.original.block_order
        const type = tableContent.row.original.type

        const color = getBidColorBasedOnOrderAndType(type, order)
        return <Icon color={color} icon={'fa fa-circle'} />
      },
    },
    {
      accessor: 'title',
      Header: t('Block bid'),
    },
    {
      accessor: 'name',
      Header: t('Name'),
    },
    {
      accessor: 'date',
      Header: t('Date'),
    },
    {
      accessor: 'displayStartTime',
      Header: t('Start time'),
    },
    {
      accessor: 'displayEndTime',
      Header: t('End time'),
    },
    {
      accessor: 'volume',
      Header: `${t('Volume')} (MW)`,
      Cell: (tableContent: any) => {
        const volume = tableContent.row.original.volume
        if (isArray(volume) && volume.length > 1) {
          const nonZeroVolume = volume.filter((value: number) => value !== 0)
          const min = nonZeroVolume.length > 0 ? Math.min(...nonZeroVolume) : 0
          const max = Math.max(...volume)
          return min === max ? min : `${min} - ${max}`
        }
        return isArray(volume) ? volume[0] : volume
      },
    },
    {
      accessor: 'price',
      Header: `${t('Avg price')} (${currency}/MWh)`,
    },
    {
      accessor: 'editable',
      Header: t('Edit'),
      Cell: (tableContent: any) => (
        <Button
          link
          icon={'fal fa-pen'}
          disabled={disabledBidButtons}
          onClick={() => editBlockBid(tableContent.row.original)}
        >
          {''}
        </Button>
      ),
    },
    {
      accessor: 'clear',
      Header: t('Remove'),
      Cell: (tableContent: any) => (
        <Button link icon={'fal fa-trash'} onClick={() => deleteBid(tableContent.row.original)}>
          {''}
        </Button>
      ),
    },
  ]

  if (elplanSnap.scenarios.length > 1) {
    const scenarioColumn = {
      accessor: 'scenarioId',
      Header: t('Scenario'),
      Cell: (tableContent: any) => (
        <span>{getScenarioName(tableContent.row.original.scenarioId, digitalTwin?.translations)}</span>
      ),
    }

    // Insert scenario column between name and date
    columns.splice(3, 0, scenarioColumn)
  }

  if (bidModalTagSelectionContext.requireTagForBlockBids) {
    const tagTypeColumn = {
      accessor: 'tagType',
      Header: t('Resource'),
      Cell: (tableContent: any) => {
        const productionConsumption = getBidTagProductionConsumption(
          bidModalTagSelectionContext.tagsForDate,
          tableContent.row.original.tag
        )
        if (productionConsumption === 'production') {
          return <span>{t('Production')}</span>
        }
        if (productionConsumption === 'consumption') {
          return <span>{t('Consumption')}</span>
        }
        return <span>{'-'}</span>
      },
    }

    // Insert the tag type column immediately before the date column (after name, or after scenario when present)
    const dateColumnIndex = columns.findIndex((column) => column.accessor === 'date')
    columns.splice(dateColumnIndex, 0, tagTypeColumn)
  }

  return (
    <div>
      {disabledBidButtons && (
        <InfoBanner text={t('You cannot create or edit bids when in week view')} style={'warning'} />
      )}
      <DefaultTable
        columns={columns}
        data={blockBids}
        initialPageSize={20}
        initialSortBy={[{ id: 'title', desc: false }]}
        autoResetAll={false}
      />
      {modalIsOpen && (
        <BidModal
          closeModal={closeModal}
          bid={currentBlockBid}
          editBid={(blockBid: FormattedBidWithIdAndTitle) => {
            if (currentBlockBid?.fake) {
              createBid(blockBid)
            } else {
              updateBid(blockBid)
            }
          }}
          type={getType(currentBlockBid?.type ?? 'blockbid')}
          isEditing
          tagsForDate={bidModalTagSelectionContext.tagsForDate}
          requireTagForBlockBids={bidModalTagSelectionContext.requireTagForBlockBids}
          bidInputCurrency={getBidInputCurrency(currentBlockBid?.type)}
        />
      )}
    </div>
  )
}
